import Select from "lib/formInputs/select/Select";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import Pagination from "react-bootstrap/Pagination";

const pageSizeRange = [10, 25, 50, 100];

function CommonPagination(props) {
  const { className, total, updatePage, module } = props;
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeRange[0]);
  const defaultCurrPage = sessionStorage.getItem(`${module}_currentPage`);

  const totalPages = useMemo(() => Math.ceil(total / pageSize), [total, pageSize]);

  useEffect(() => {
    if (module) {
      const currPage = sessionStorage.getItem(`${module}_currentPage`);
      const pgSize = sessionStorage.getItem(`${module}_pageSize`);
      setCurrentPage(typeof currPage === "string" ? parseInt(currPage) : currentPage);
      setPageSize(typeof pgSize === "string" ? parseInt(pgSize) : pageSize);
    }
  }, [defaultCurrPage]);

  /******************* 
    @purpose :used for create array for page number
    @Parameter : {} 
    @Author : INIC
    ******************/
  const pageCount = useMemo(() => new Array(totalPages || 0).fill().map((_, i) => i + 1), [totalPages]);

  const onPageChange = (value) => {
    if (value !== currentPage) {
      setCurrentPage(value);
      updatePage(value, pageSize);
    }
  };

  const onPageSizeChange = (value) => {
    if (value !== pageSize) {
      setPageSize(value);
      setCurrentPage(1);
      updatePage(1, value);
    }
  };

  return (
    <>
      {total > 0 && (
        <div className="pt-18 table-footer">
          <div className="show-pagination-results ">
            {total > 10 && (
              <div className="flex-middle">
                <span className="text-gray me-10">Showing</span>
                <Select
                  value={
                    pageSize
                      ? {
                          label: pageSize,
                          value: pageSize,
                        }
                      : undefined
                  }
                  options={pageSizeRange.map((number) => ({
                    label: number,
                    value: number,
                  }))}
                  changeHandler={(e) => {
                    onPageSizeChange(parseInt(e.value));
                  }}
                  onKeyDown={(e) => {
                    e.preventDefault();
                  }}
                />
              </div>
            )}
          </div>
          <span className="text-gray text-nowrap fs-12 px-8 align-self-center showing-results">
            Showing {currentPage > 1 ? (currentPage - 1) * pageSize + 1 : 1} to{" "}
            {currentPage * pageSize < total ? currentPage * pageSize : total} out of {total} records
          </span>

          <Pagination className={className}>
            <Pagination.Prev disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)} />
            {totalPages <= 5 &&
              pageCount.map((num) => (
                <Pagination.Item onClick={() => onPageChange(num)} key={num} active={num === currentPage}>
                  {num}
                </Pagination.Item>
              ))}
            {totalPages > 5 && (
              <>
                <Pagination.Item onClick={() => onPageChange(1)} key={1} active={1 === currentPage}>
                  {1}
                </Pagination.Item>
                {currentPage <= 3 && (
                  <>
                    <Pagination.Item onClick={() => onPageChange(2)} key={2} active={2 === currentPage}>
                      {2}
                    </Pagination.Item>
                    <Pagination.Item onClick={() => onPageChange(3)} key={3} active={3 === currentPage}>
                      {3}
                    </Pagination.Item>
                    <Pagination.Item onClick={() => onPageChange(4)} key={4} active={4 === currentPage}>
                      {4}
                    </Pagination.Item>
                  </>
                )}
                {currentPage > 3 && <Pagination.Ellipsis />}
                {currentPage > 3 && currentPage === totalPages && (
                  <Pagination.Item onClick={() => onPageChange(currentPage - 2)} key={currentPage - 2} active={false}>
                    {currentPage - 2}
                  </Pagination.Item>
                )}
                {currentPage > 3 && (
                  <Pagination.Item onClick={() => onPageChange(currentPage - 1)} key={currentPage - 1} active={false}>
                    {currentPage - 1}
                  </Pagination.Item>
                )}
                {currentPage > 3 && currentPage !== totalPages && (
                  <Pagination.Item onClick={() => onPageChange(currentPage)} key={currentPage} active={true}>
                    {currentPage}
                  </Pagination.Item>
                )}
                {currentPage > 3 && currentPage < totalPages - 1 && (
                  <Pagination.Item onClick={() => onPageChange(currentPage + 1)} key={currentPage + 1} active={false}>
                    {currentPage + 1}
                  </Pagination.Item>
                )}
                {currentPage < totalPages - 2 && <Pagination.Ellipsis />}
                <Pagination.Item
                  onClick={() => onPageChange(totalPages)}
                  key={totalPages}
                  active={totalPages === currentPage}
                >
                  {totalPages}
                </Pagination.Item>
              </>
            )}
            <Pagination.Next disabled={currentPage === totalPages} onClick={() => onPageChange(currentPage + 1)} />
          </Pagination>
        </div>
      )}
    </>
  );
}

export const defaultProps = {
  className: "",
  total: 0,
  updatePage: () => null,
};

export const propTypes = {
  className: PropTypes.string,
  total: PropTypes.number,
  updatePage: PropTypes.func,
  module: PropTypes.object.isRequired,
};

CommonPagination.defaultProps = defaultProps;
CommonPagination.propTypes = propTypes;

export default React.memo(CommonPagination);
